import React, { useEffect, useId, useState } from 'react';
import { ArrowLeftRight, Trash2 } from 'lucide-react';
import { Button, Dialog, DialogBody, DialogFooter, DialogHeader, Field, Input, Select, Textarea } from '../../../components/ui';
import { ConfirmModal } from '../../../components/common/ConfirmModal';
import { createRelationship, deleteRelationship, updateRelationship } from '../../../services/graphService';
import type { TaxonomyItem } from '../../../services/taxonomiesService';
import type { NetworkLink, NetworkNode, RelationKind } from '../types';
import { TYPE_LABEL, inferRelationKind } from './networkModel';
import { EntityAvatar } from './ui';

const PLACEHOLDER: Record<RelationKind, string> = {
  oo: 'e.g. Subsidiary, Partner, Regulator of',
  os: 'e.g. Board member, Advisor, Shareholder',
  ss: 'e.g. Mentor, Family tie, Business partner',
};

const errorOf = (e: any, fallback: string) => {
  const m = e?.response?.data?.message;
  return Array.isArray(m) ? m.join(', ') : m || e?.message || fallback;
};

interface FormState {
  label: string;
  category: string;
  description: string;
  weight: number;
  startDate: string;
  endDate: string;
}

const StrengthPicker: React.FC<{ value: number; onChange: (v: number) => void; id: string }> = ({ value, onChange, id }) => (
  <div id={id} role="radiogroup" aria-label="Strength" className="flex h-10 items-center gap-1 sm:h-9">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        role="radio"
        aria-checked={value === n}
        title={['Weak', 'Light', 'Moderate', 'Strong', 'Very strong'][n - 1]}
        onClick={() => onChange(n)}
        className="group flex h-full flex-1 items-center justify-center rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
      >
        <span className={`h-1.5 w-full rounded-full transition-colors ${n <= value ? 'bg-ink' : 'bg-neutral-200 group-hover:bg-neutral-300'}`} />
      </button>
    ))}
  </div>
);

const Fields: React.FC<{
  form: FormState;
  setForm: (f: FormState) => void;
  type: RelationKind;
  categories: TaxonomyItem[];
  idPrefix: string;
}> = ({ form, setForm, type, categories, idPrefix }) => (
  <>
    <Field label="Label" htmlFor={`${idPrefix}-label`}>
      <Input id={`${idPrefix}-label`} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder={PLACEHOLDER[type]} />
    </Field>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Field label="Category" htmlFor={`${idPrefix}-category`}>
        <Select id={`${idPrefix}-category`} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
          <option value="">None</option>
          {form.category && !categories.some((c) => c.key === form.category) && <option value={form.category}>{form.category}</option>}
          {categories.map((c) => (
            <option key={c.id} value={c.key}>
              {c.label}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Strength" htmlFor={`${idPrefix}-weight`}>
        <StrengthPicker id={`${idPrefix}-weight`} value={form.weight} onChange={(weight) => setForm({ ...form, weight })} />
      </Field>
    </div>
    <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2">
      <Field label="Since" htmlFor={`${idPrefix}-start`}>
        <Input id={`${idPrefix}-start`} type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
      </Field>
      <Field label="Until" htmlFor={`${idPrefix}-end`}>
        <Input id={`${idPrefix}-end`} type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
      </Field>
    </div>
    <Field label="Description" htmlFor={`${idPrefix}-description`}>
      <Textarea
        id={`${idPrefix}-description`}
        className="resize-none"
        rows={3}
        value={form.description}
        onChange={(e) => setForm({ ...form, description: e.target.value })}
        placeholder="Context, sources"
      />
    </Field>
  </>
);

export const CreateLinkDialog: React.FC<{
  a: NetworkNode | null;
  b: NetworkNode | null;
  categories: TaxonomyItem[];
  onClose: () => void;
  onCreated: () => void;
}> = ({ a, b, categories, onClose, onCreated }) => {
  const titleId = useId();
  const idPrefix = useId();
  const [form, setForm] = useState<FormState>({ label: '', category: '', description: '', weight: 2, startDate: '', endDate: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = !!a && !!b;

  useEffect(() => {
    if (open) {
      setForm({ label: '', category: '', description: '', weight: 2, startDate: '', endDate: '' });
      setError(null);
    }
  }, [open, a?.id, b?.id]);

  if (!a || !b) return null;
  const type = inferRelationKind(a.type, b.type);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await createRelationship({
        sourceType: a.type,
        sourceId: a.id,
        targetType: b.type,
        targetId: b.id,
        type,
        label: form.label || undefined,
        description: form.description || undefined,
        category: form.category || undefined,
        weight: form.weight,
        startDate: form.startDate || undefined,
        endDate: form.endDate || undefined,
      });
      onCreated();
    } catch (e) {
      setError(errorOf(e, 'Could not create the relationship'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} labelledBy={titleId} handle className="max-h-[92dvh] sm:max-w-md">
      <DialogHeader id={titleId} title="New relationship" eyebrow={TYPE_LABEL[type]} onClose={onClose} />
      <DialogBody className="space-y-3">
        <div className="flex items-center gap-2 rounded-xl bg-neutral-50 px-3 py-2.5 text-[13px] text-ink">
          <span className="flex min-w-0 flex-1 items-center gap-2">
            <EntityAvatar node={a} size="xs" />
            <span className="truncate">{a.label}</span>
          </span>
          <ArrowLeftRight className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
          <span className="flex min-w-0 flex-1 items-center justify-end gap-2">
            <span className="truncate">{b.label}</span>
            <EntityAvatar node={b} size="xs" />
          </span>
        </div>
        <Fields form={form} setForm={setForm} type={type} categories={categories} idPrefix={idPrefix} />
        {error && <p className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">{error}</p>}
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" loading={saving} onClick={submit}>
          Create
        </Button>
      </DialogFooter>
    </Dialog>
  );
};

export const EditLinkDialog: React.FC<{
  link: NetworkLink | null;
  nodesById: Map<string, NetworkNode>;
  categories: TaxonomyItem[];
  onClose: () => void;
  onChanged: () => void;
}> = ({ link, nodesById, categories, onClose, onChanged }) => {
  const titleId = useId();
  const idPrefix = useId();
  const [form, setForm] = useState<FormState>({ label: '', category: '', description: '', weight: 2, startDate: '', endDate: '' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!link) return;
    // Older relationships stored the category label; the taxonomy key is canonical
    const stored = link.category || '';
    const match = categories.find((c) => c.key === stored || c.label.toLowerCase() === stored.toLowerCase());
    setForm({
      label: link.label || '',
      category: match ? match.key : stored,
      description: link.description || '',
      weight: link.weight || 2,
      startDate: link.startDate ? String(link.startDate).slice(0, 10) : '',
      endDate: link.endDate ? String(link.endDate).slice(0, 10) : '',
    });
    setError(null);
    setConfirm(false);
    // categories may arrive after the dialog opens; their array identity is not stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [link, categories.length]);

  if (!link?.relationshipId) return null;
  const a = nodesById.get(link.source);
  const b = nodesById.get(link.target);
  const id = link.relationshipId;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateRelationship(id, {
        label: form.label || undefined,
        description: form.description || undefined,
        category: form.category || undefined,
        weight: form.weight,
        startDate: form.startDate || undefined,
        endDate: form.endDate || null,
      });
      onChanged();
    } catch (e) {
      setError(errorOf(e, 'Could not save the relationship'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await deleteRelationship(id);
      setConfirm(false);
      onChanged();
    } catch (e) {
      setConfirm(false);
      setError(errorOf(e, 'Could not delete the relationship'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={!!link && !confirm} onClose={onClose} labelledBy={titleId} handle className="max-h-[92dvh] sm:max-w-md">
        <DialogHeader id={titleId} title="Edit relationship" eyebrow={`${a?.label || '—'} · ${b?.label || '—'}`} onClose={onClose} />
        <DialogBody className="space-y-3">
          <Fields form={form} setForm={setForm} type={link.type} categories={categories} idPrefix={idPrefix} />
          {error && <p className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">{error}</p>}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" className="mr-auto !text-primary-700 hover:!bg-primary-50" onClick={() => setConfirm(true)}>
            <Trash2 className="h-4 w-4" strokeWidth={1.75} />
            Delete
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={save}>
            Save
          </Button>
        </DialogFooter>
      </Dialog>
      <ConfirmModal
        open={confirm}
        title="Delete relationship"
        message={
          <span>
            Delete {link.label ? <>“{link.label}” </> : 'the relationship '}between {a?.label || '—'} and {b?.label || '—'}? This cannot be undone.
          </span>
        }
        confirmText="Delete"
        loading={deleting}
        onCancel={() => setConfirm(false)}
        onConfirm={remove}
      />
    </>
  );
};
